import React from "react";
import "./App.css";
import Header from "./components/Header";
import Hero from "./components/Hero";
import PropertySlider from "./components/PropertySlider";
import PropertyIntro from "./components/PropertyIntro";
import Properties from "./components/Properties";
import Footer from "./components/Footer";
import VitrineConfig from "./components/VitrineConfig";
import "./css/dark-mode.css";
import {
  FONT_FAMILY_OPTIONS,
  createDefaultVitrineConfig,
  loadVitrineConfig,
  resetVitrineConfig,
  saveVitrineConfig,
} from "./config";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

function App() {
  const { pathname } = useLocation();
  const isPropertiesPage =
    pathname === "/properties" || pathname === "/properties.html";
  const isConfigPage = pathname === "/configuracao";
  const [isDarkMode, setIsDarkMode] = React.useState(
    () => window.localStorage.getItem("imob-crm-vitrine:dark-mode") === "dark",
  );
  const [configState, setConfigState] = React.useState(() => {
    try {
      return { config: loadVitrineConfig(), error: "" };
    } catch (error) {
      return {
        config: createDefaultVitrineConfig(),
        error: `Não foi possível carregar a configuração salva: ${error.message}`,
      };
    }
  });

  const handleSaveConfig = (config) => {
    const savedConfig = saveVitrineConfig(config);
    setConfigState({ config: savedConfig, error: "" });
  };

  const handleResetConfig = () => {
    const defaultConfig = resetVitrineConfig();
    setConfigState({ config: defaultConfig, error: "" });
    return defaultConfig;
  };

  const handlePropertiesColumnsChange = (propertiesColumns) => {
    try {
      const savedConfig = saveVitrineConfig({
        ...configState.config,
        propertiesColumns,
      });
      setConfigState({ config: savedConfig, error: "" });
    } catch (error) {
      setConfigState((currentState) => ({
        ...currentState,
        error: `Não foi possível salvar a quantidade de cartões por linha: ${error.message}`,
      }));
    }
  };

  const toggleDarkMode = () => {
    const nextMode = isDarkMode ? "light" : "dark";
    window.localStorage.setItem("imob-crm-vitrine:dark-mode", nextMode);
    setIsDarkMode(nextMode === "dark");
  };

  const sectionComponents = {
    hero: Hero,
    "featured-properties": PropertySlider,
    "property-intro": PropertyIntro,
  };

  return (
    <div
      className={`App${isConfigPage ? " App--config" : ""}`}
      data-theme={isDarkMode ? "dark" : "light"}
      style={{
        "--vitrine-theme-color": configState.config.themeColor,
        "--vitrine-secondary-color": configState.config.secondaryColor,
        "--vitrine-font-family":
          FONT_FAMILY_OPTIONS[configState.config.fontFamily],
      }}
    >
      <button
        className="theme-toggle"
        type="button"
        onClick={toggleDarkMode}
        aria-label={isDarkMode ? "Ativar modo claro" : "Ativar modo escuro"}
        aria-pressed={isDarkMode}
        title={isDarkMode ? "Ativar modo claro" : "Ativar modo escuro"}
      >
        {isDarkMode ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.3 15.3A8.5 8.5 0 0 1 8.7 3.7 8.5 8.5 0 1 0 20.3 15.3Z" />
          </svg>
        )}
      </button>
      {!isConfigPage && (
        <Header
          headerItems={configState.config.headerItems}
          logoUrl={configState.config.logoUrl}
          companyName={configState.config.companyName}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={
            <>
              {configState.config.sections
                .filter((section) => section.visible !== false)
                .map((section) => {
                  const Section = sectionComponents[section.id];
                  return (
                    <div
                      className="vitrine-section"
                      key={section.id}
                      style={{
                        "--vitrine-theme-color":
                          section.themeColor || configState.config.themeColor,
                        ...(section.backgroundColor && {
                          "--vitrine-section-background-color":
                            section.backgroundColor,
                        }),
                      }}
                    >
                      <Section
                        title={section.title}
                        supportingText={section.supportingText}
                        quickLinks={configState.config.quickLinks}
                        backgroundImages={configState.config.heroBackgrounds}
                      />
                    </div>
                  );
                })}
            </>
          }
        />
        <Route
          path="/configuracao"
          element={
            <VitrineConfig
              config={configState.config}
              initialError={configState.error}
              onSave={handleSaveConfig}
              onReset={handleResetConfig}
            />
          }
        />
        <Route
          path="/properties"
          element={
            <Properties
              cardsPerRow={configState.config.propertiesColumns}
              onCardsPerRowChange={handlePropertiesColumnsChange}
              configError={configState.error}
            />
          }
        />
        <Route
          path="/properties.html"
          element={
            <Properties
              cardsPerRow={configState.config.propertiesColumns}
              onCardsPerRowChange={handlePropertiesColumnsChange}
              configError={configState.error}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer
        showMore={
          !isPropertiesPage && !isConfigPage && configState.config.showMore
        }
      />
    </div>
  );
}

export default App;
